// ============================================================
// i18n CONFIGURATION — SHARED
// Used by both web (react-i18next) and mobile (react-i18next/native)
// All 38 supported locales are registered here.
// Phase 1: en + hi fully translated.
// All others: stub files — machine translate before Phase 2 launch.
// ============================================================

export const SUPPORTED_LOCALES = [
  // Primary
  { code: 'en',    name: 'English',              nativeName: 'English',      rtl: false, group: 'primary'  },

  // Indian Scheduled Languages
  { code: 'hi',    name: 'Hindi',                nativeName: 'हिंदी',         rtl: false, group: 'indian'   },
  { code: 'bn',    name: 'Bengali',              nativeName: 'বাংলা',          rtl: false, group: 'indian'   },
  { code: 'te',    name: 'Telugu',               nativeName: 'తెలుగు',         rtl: false, group: 'indian'   },
  { code: 'mr',    name: 'Marathi',              nativeName: 'मराठी',          rtl: false, group: 'indian'   },
  { code: 'ta',    name: 'Tamil',                nativeName: 'தமிழ்',          rtl: false, group: 'indian'   },
  { code: 'ur',    name: 'Urdu',                 nativeName: 'اردو',           rtl: true,  group: 'indian'   },
  { code: 'gu',    name: 'Gujarati',             nativeName: 'ગુજરાતી',        rtl: false, group: 'indian'   },
  { code: 'kn',    name: 'Kannada',              nativeName: 'ಕನ್ನಡ',           rtl: false, group: 'indian'   },
  { code: 'ml',    name: 'Malayalam',            nativeName: 'മലയാളം',         rtl: false, group: 'indian'   },
  { code: 'or',    name: 'Odia',                 nativeName: 'ଓଡ଼ିଆ',           rtl: false, group: 'indian'   },
  { code: 'pa',    name: 'Punjabi',              nativeName: 'ਪੰਜਾਬੀ',         rtl: false, group: 'indian'   },
  { code: 'as',    name: 'Assamese',             nativeName: 'অসমীয়া',        rtl: false, group: 'indian'   },
  { code: 'mai',   name: 'Maithili',             nativeName: 'मैथिली',         rtl: false, group: 'indian'   },
  { code: 'kok',   name: 'Konkani',              nativeName: 'कोंकणी',          rtl: false, group: 'indian'   },
  { code: 'doi',   name: 'Dogri',                nativeName: 'डोगरी',           rtl: false, group: 'indian'   },
  { code: 'mni',   name: 'Manipuri',             nativeName: 'মৈতৈলোন্',        rtl: false, group: 'indian'   },
  { code: 'bo',    name: 'Bodo',                 nativeName: 'बड़ो',            rtl: false, group: 'indian'   },
  { code: 'ks',    name: 'Kashmiri',             nativeName: 'کشمیری',          rtl: true,  group: 'indian'   },
  { code: 'sd',    name: 'Sindhi',               nativeName: 'سنڌي',            rtl: true,  group: 'indian'   },
  { code: 'ne',    name: 'Nepali',               nativeName: 'नेपाली',          rtl: false, group: 'indian'   },
  { code: 'sa',    name: 'Sanskrit',             nativeName: 'संस्कृतम्',       rtl: false, group: 'indian'   },
  { code: 'sat',   name: 'Santali',              nativeName: 'ᱥᱟᱱᱛᱟᱲᱤ',       rtl: false, group: 'indian'   },

  // East Asian
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '中文（简体）',     rtl: false, group: 'asian'    },
  { code: 'zh-TW', name: 'Chinese (Traditional)',nativeName: '中文（繁體）',     rtl: false, group: 'asian'    },
  { code: 'ko',    name: 'Korean',               nativeName: '한국어',           rtl: false, group: 'asian'    },
  { code: 'ja',    name: 'Japanese',             nativeName: '日本語',           rtl: false, group: 'asian'    },

  // European
  { code: 'fr',    name: 'French',               nativeName: 'Français',        rtl: false, group: 'european' },
  { code: 'de',    name: 'German',               nativeName: 'Deutsch',         rtl: false, group: 'european' },
  { code: 'es',    name: 'Spanish',              nativeName: 'Español',         rtl: false, group: 'european' },
  { code: 'it',    name: 'Italian',              nativeName: 'Italiano',        rtl: false, group: 'european' },
  { code: 'pt',    name: 'Portuguese',           nativeName: 'Português',       rtl: false, group: 'european' },
  { code: 'nl',    name: 'Dutch',                nativeName: 'Nederlands',      rtl: false, group: 'european' },
  { code: 'pl',    name: 'Polish',               nativeName: 'Polski',          rtl: false, group: 'european' },
  { code: 'ro',    name: 'Romanian',             nativeName: 'Română',          rtl: false, group: 'european' },
  { code: 'cs',    name: 'Czech',                nativeName: 'Čeština',         rtl: false, group: 'european' },
  { code: 'sv',    name: 'Swedish',              nativeName: 'Svenska',         rtl: false, group: 'european' },
  { code: 'da',    name: 'Danish',               nativeName: 'Dansk',           rtl: false, group: 'european' },
  { code: 'no',    name: 'Norwegian',            nativeName: 'Norsk',           rtl: false, group: 'european' },
  { code: 'fi',    name: 'Finnish',              nativeName: 'Suomi',           rtl: false, group: 'european' },
] as const;

export type LocaleCode = typeof SUPPORTED_LOCALES[number]['code'];

/** Locales that require RTL layout mirroring */
export const RTL_LOCALES: LocaleCode[] = ['ur', 'ks', 'sd'];

/** Default locale */
export const DEFAULT_LOCALE: LocaleCode = 'en';

/** Fully translated locales (Phase 1) */
export const READY_LOCALES: LocaleCode[] = ['en', 'hi'];

/** Returns true if a locale requires RTL layout */
export function isRTL(code: string): boolean {
  return RTL_LOCALES.includes(code as LocaleCode);
}

/** Get locale metadata by code */
export function getLocale(code: string) {
  return SUPPORTED_LOCALES.find((l) => l.code === code) ?? SUPPORTED_LOCALES[0];
}
